import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    confirmJobPoolUpload,
    detectJobPoolContentType,
    getJobPoolUploadUrl,
    UPLOAD_ERROR_MESSAGES,
    uploadJobPoolFile,
} from '../services/jobPoolApi.js';

/**
 * 求人ファイルの取込状況 (画面レイアウト_SCR-M-001.md §6.2「取込状況の保持」、Issue #759)
 *
 * 取込の列と取込エントリ (DP-M-010) をルーティングより上に持ち、画面を離れても取込を続け、
 * 戻ったときに状況を表示できるようにする。ブラウザの保存領域には書かない
 * (送信前の File は保存できず、再読込後に処理中の行が実際の結果と食い違うため)。
 */
const JobUploadContext = createContext(null);

const ACTIVE_STATUSES = ['waiting', 'uploading', 'extracting'];

let nextEntryId = 0;

/** 1 ファイルを URL 発行 → GCS 送信 → 取込確定の順に取り込み、状態を onStatus で知らせる。 */
async function runUpload(file, opportunityId, onStatus) {
    onStatus({ status: 'uploading' });
    const contentType = detectJobPoolContentType(file);

    const urlResult = await getJobPoolUploadUrl(contentType);
    if (!urlResult.ok) return urlResult;

    const uploadResult = await uploadJobPoolFile(urlResult.uploadUrl, file, contentType);
    if (!uploadResult.ok) return uploadResult;

    onStatus({ status: 'extracting' });
    const confirmResult = await confirmJobPoolUpload({
        blobName: urlResult.blobName,
        fileName: file.name,
        targetCandidateId: opportunityId,
    });
    // 取込確定は AI ポイントを消費しうる (成否を問わない)。残量の取り直しの契機に使うため、
    // 取込確定まで進んだことを呼出側に伝える (画面レイアウト_SCR-M-001.md §6.4.4「取り直す時機」③、Issue #882)。
    // ここまで進まなかった失敗 (URL 発行・GCS 送信) はポイントを消費しないため印を付けない
    return { ...confirmResult, confirmAttempted: true };
}

/**
 * @param {{children: React.ReactNode, fileStore?: Map}} props
 *   fileStore: 取込前・エラーの行の File を持つ入れ物 (entryId -> { file, opportunityId })。
 *   通常は指定しない。テストで File を放したことを確かめるときだけ渡す
 */
export function JobUploadProvider({ children, fileStore }) {
    const [entries, setEntries] = useState([]);
    // ボタン操作時に最新の行を参照する (操作はレンダー後に起きるため、直近のレンダーの値で足りる)
    const entriesRef = useRef(entries);
    entriesRef.current = entries;
    const filesRef = useRef(fileStore ?? new Map());
    const queueRef = useRef([]);
    const processingRef = useRef(false);
    const listenersRef = useRef(new Set()); // { opportunityId, callback }
    // 取込確定の結果が決まったことを知らせる先 (案件を問わない。Issue #882)
    const confirmSettledListenersRef = useRef(new Set());

    const updateEntry = useCallback((id, patch) => {
        setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    }, []);

    const notifyComplete = useCallback((opportunityId, jobPoolItem) => {
        listenersRef.current.forEach((listener) => {
            if (listener.opportunityId === opportunityId) listener.callback(jobPoolItem);
        });
    }, []);

    const notifyConfirmSettled = useCallback(() => {
        confirmSettledListenersRef.current.forEach((callback) => callback());
    }, []);

    const processOne = useCallback(async (id) => {
        const target = filesRef.current.get(id);
        if (!target) return;

        let result;
        // 取込確定まで進んだか (status が 'extracting' になるのは取込確定の直前)。
        // 例外で終わったときに、ポイントを消費しうるかを判別する (Issue #882)
        let reachedConfirm = false;
        try {
            result = await runUpload(target.file, target.opportunityId, (patch) => {
                if (patch.status === 'extracting') reachedConfirm = true;
                updateEntry(id, patch);
            });
        } catch {
            // §6.3 No.1〜8 に当てはまらない想定外の例外。API 関数は例外を返さないため、取込確定の応答を
            // 受け取った後の処理でだけ起こりうる。サーバで取込が済んでいる場合があるため No.8 とし、列は止めない
            result = {
                ok: false,
                errorMessage: UPLOAD_ERROR_MESSAGES.confirmConnectionLost,
                retryable: true,
                confirmAttempted: reachedConfirm,
            };
        }
        if (!result.ok) {
            updateEntry(id, { status: 'error', errorMessage: result.errorMessage, retryable: result.retryable });
            if (result.confirmAttempted) notifyConfirmSettled();
            return;
        }
        // 再試行が要るのはエラーの行だけのため、取り込めたファイルはメモリから放す (1 件最大 30MB)
        filesRef.current.delete(id);
        // version > 1 は重複判定で既存の求人プールアイテムが更新されたことを示す (Issue #585)
        updateEntry(id, { status: result.data.version > 1 ? 'duplicate' : 'complete' });
        notifyComplete(target.opportunityId, result.data);
        if (result.confirmAttempted) notifyConfirmSettled();
    }, [updateEntry, notifyComplete, notifyConfirmSettled]);

    const processQueue = useCallback(async () => {
        if (processingRef.current) return;
        processingRef.current = true;
        // 取込の列はタブに 1 本で、1 件ずつ順に取り込む (§6.2。抽出が AI ポイントを消費するため)
        try {
            while (queueRef.current.length > 0) {
                await processOne(queueRef.current.shift());
            }
        } finally {
            processingRef.current = false;
        }
    }, [processOne]);

    const enqueueFiles = useCallback((opportunityId, files) => {
        const added = files.map((file) => {
            const id = ++nextEntryId;
            filesRef.current.set(id, { file, opportunityId });
            queueRef.current.push(id);
            return { id, opportunityId, fileName: file.name, status: 'waiting', errorMessage: null, retryable: false };
        });
        setEntries((prev) => [...prev, ...added]);
        processQueue();
    }, [processQueue]);

    const findEntry = useCallback((id) => entriesRef.current.find((e) => e.id === id), []);

    // 再試行はエラーかつ再試行可の行だけ (BTN-M-008)。同じ行を列の最後に戻し、URL 発行からやり直す
    const retryEntry = useCallback((id) => {
        const entry = findEntry(id);
        if (!entry || entry.status !== 'error' || !entry.retryable || !filesRef.current.has(id)) return;
        updateEntry(id, { status: 'waiting', errorMessage: null, retryable: false });
        queueRef.current.push(id);
        processQueue();
    }, [findEntry, updateEntry, processQueue]);

    // 閉じるはエラーの行だけ (BTN-M-009)。画面の行を消すだけで、求人プール・サーバには何もしない
    const dismissEntry = useCallback((id) => {
        const entry = findEntry(id);
        if (!entry || entry.status !== 'error') return;
        filesRef.current.delete(id);
        setEntries((prev) => prev.filter((e) => e.id !== id));
    }, [findEntry]);

    const subscribeComplete = useCallback((opportunityId, callback) => {
        const listener = { opportunityId, callback };
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
    }, []);

    const subscribeConfirmSettled = useCallback((callback) => {
        confirmSettledListenersRef.current.add(callback);
        return () => confirmSettledListenersRef.current.delete(callback);
    }, []);

    const hasActiveUpload = entries.some((e) => ACTIVE_STATUSES.includes(e.status));

    // 取込中の再読込・タブを閉じる操作でブラウザ標準の離脱確認を出す (§6.2「取込中にページを離れるときの確認」)
    useEffect(() => {
        if (!hasActiveUpload) return undefined;
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasActiveUpload]);

    const value = useMemo(
        () => ({ entries, enqueueFiles, retryEntry, dismissEntry, subscribeComplete, subscribeConfirmSettled }),
        [entries, enqueueFiles, retryEntry, dismissEntry, subscribeComplete, subscribeConfirmSettled]
    );

    return <JobUploadContext.Provider value={value}>{children}</JobUploadContext.Provider>;
}

export function useJobUpload() {
    const context = useContext(JobUploadContext);
    if (!context) {
        throw new Error('useJobUpload は JobUploadProvider の内側で使用してください');
    }
    return context;
}

/** 指定した案件の求人ファイルが 1 件取り込まれるたびに callback を呼ぶ (§6.2 手順 4)。 */
export function useJobUploadCompleted(opportunityId, callback) {
    const { subscribeComplete } = useJobUpload();
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(
        () => subscribeComplete(opportunityId, (jobPoolItem) => callbackRef.current?.(jobPoolItem)),
        [opportunityId, subscribeComplete]
    );
}

/**
 * 取込確定の結果が決まるたびに callback を呼ぶ (§6.4.4「取り直す時機」③、Issue #882)。
 *
 * 完了・エラー・通信の失敗を問わず、どの案件のファイルでも呼ぶ (取込確定まで進めば
 * AI ポイントを消費しうるため)。呼ぶのは購読している間だけで、画面を表示していない間の
 * 取込は次に表示したときの取得 (§6.1) で反映される。
 */
export function useJobUploadConfirmSettled(callback) {
    const { subscribeConfirmSettled } = useJobUpload();
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => subscribeConfirmSettled(() => callbackRef.current?.()), [subscribeConfirmSettled]);
}
